import * as React from 'react';
import showdown from 'showdown';
import { 
  getAiAnswer, 
  getAgentsToBeUsed, 
  generateMetrics,
  generateFilters, 
  generateHavingFilters, 
  generateChartOptions, 
  generateCustomMetrics, 
  generateCustomAggregateMetrics 
} from '../../../api/shipsyAI';
import { sendMessage } from '../persistentIframeRenderer/iframeMessageUtils';
import { IframeApps } from '../persistentIframeRenderer/constants';
import { 
  Message, 
  ToolCall, 
  setExecutionLayers, 
  updateLayerStatus, 
  updateToolCallStatus, 
  setCurrentLayer 
} from '../../../actions/chatActions';
import { AgentType, AnalyticsContext, AgentExecutionData } from './types';
import { AGENT_EXECUTION_MAP } from './constants';

export const createUserMessage = (content: string): Message => ({
  content,
  sender: 'user',
  timestamp: new Date(),
});

export const createAiMessage = (content: string, formattedContent?: React.ReactElement): Message => ({
  content,
  sender: 'ai',
  timestamp: new Date(),
  formattedContent,
});

export const createToolCall = (name: string, content: any, layerIndex?: number, totalLayers?: number): ToolCall => ({
  name,
  timestamp: new Date(),
  isExpanded: false,
  sender: 'toolCall',
  content,
  status: 'pending',
  layerIndex,
  totalLayers,
  id: `${name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  startTime: new Date(),
});

export const formatAiResponse = (response: string): React.ReactElement => {
  const converter = new showdown.Converter();
  const htmlText = converter.makeHtml(response);
  const modifiedContent = htmlText
    .replace(/<ol>/g, '<p>')
    .replace(/<\/ol>/g, '</p>');
  return React.createElement('div', { dangerouslySetInnerHTML: { __html: modifiedContent } });
};

export const sendIframeMessage = (messageType: string, data: any) => {
  try {
    const message = {
      type: messageType,
      data: data,
      source: 'AskAiDrawer'
    };
    sendMessage(IframeApps.GENERIC_ANALYTICS, message);
  } catch (error) {
    console.error('Failed to send iframe message:', error);
  }
};

export const handleSupportAgent = async (
  content: string, 
  messages: (Message | ToolCall)[], 
  userTimezone: string
): Promise<Message> => {
  const query = `${content}. User Timezone: ${userTimezone}`;
  const previousConversation = JSON.stringify(messages);
  const response = await getAiAnswer({ query, previous_conversation: previousConversation });
  
  const aiMessage = createAiMessage(response.output);
  aiMessage.formattedContent = formatAiResponse(response.output);
  
  return aiMessage;
};

export const executeAgent = async (
  agentType: string, 
  apiFunction: Function, 
  apiName: string, 
  agentData: AgentExecutionData,
  dispatch: any,
  toolCallId: string
) => {
  try {
    // Update tool call status to running
    dispatch(updateToolCallStatus(toolCallId, 'running'));
    
    const result = await apiFunction(agentData);
    
    // Update tool call status to completed with result
    dispatch(updateToolCallStatus(toolCallId, 'completed', result));
    
    const toolCall = createToolCall(apiName, result);
    toolCall.id = toolCallId;
    toolCall.status = 'completed';
    toolCall.content = result;
    
    sendIframeMessage('ASK_AI_RESPONSE', {
      api: apiName,
      result: result,
      timestamp: new Date().toISOString()
    });
    
    return { agent: agentType, result, toolCall };
  } catch (error) {
    // Update tool call status to failed
    dispatch(updateToolCallStatus(toolCallId, 'failed', undefined, error.message));
    
    return { agent: agentType, error };
  }
};

export const getAgentExecutionFunction = (agentType: string) => {
  switch (agentType) {
    case 'filters':
    case 'generateFilters':
      return generateFilters;
    case 'metric':
    case 'generateMetrics':
      return generateMetrics;
    case 'chartOptions':
    case 'generateChartOptions':
      return generateChartOptions;
    case 'havingFilters':
    case 'generateHavingFilters':
      return generateHavingFilters;
    case 'customMetrics':
    case 'generateCustomMetrics':
      return generateCustomMetrics;
    case 'customAggregateMetrics':
    case 'generateCustomAggregateMetrics':
      return generateCustomAggregateMetrics;
    default:
      throw new Error(`Unknown agent type: ${agentType}`);
  }
};

/**
 * Executes a single layer of agents sequentially
 */
export const executeLayer = async (
  layer: any,
  layerIndex: number,
  content: string,
  analyticsContext: AnalyticsContext,
  previousConversation: string,
  allResults: any,
  dispatch: any,
  onToolCallComplete: (toolCall: ToolCall) => void
) => {
  // Set current layer
  dispatch(setCurrentLayer(layerIndex));
  
  // Update layer status to running
  dispatch(updateLayerStatus(layerIndex, 'running'));
  
  const layerPromises: Promise<any>[] = [];
  const agents = layer.agents || layer;
  
  for (const agent of agents) {
    const agentData: AgentExecutionData = {
      prompt: content,
      type: analyticsContext.reportMetadata?.selectedReport,
      analyticsContext: analyticsContext,
      previous_conversation: previousConversation,
      toolCalls: allResults
    };
    
    if (allResults.metrics) {
      agentData.metrics = allResults.metrics;
    }
    
    const apiFunction = getAgentExecutionFunction(agent.agent);
    const apiName = AGENT_EXECUTION_MAP[agent.agent as keyof typeof AGENT_EXECUTION_MAP];
    
    // Create tool call for this agent
    const toolCall = createToolCall(apiName, null, layerIndex, agents.length);
    onToolCallComplete(toolCall);
    
    layerPromises.push(executeAgent(agent.agent, apiFunction, apiName, agentData, dispatch, toolCall.id!));
  }
  
  if (layerPromises.length > 0) {
    const layerResults = await Promise.all(layerPromises);
    const results: any = {};
    
    layerResults.forEach(({ agent, result, error }) => {
      if (result) {
        results[agent] = result;
      } else if (error) {
        console.error(`Error in agent ${agent}:`, error);
      }
    });
    
    // Update layer status to completed
    dispatch(updateLayerStatus(layerIndex, 'completed', results));
    
    return results;
  }
  
  return {};
};

/**
 * Runs BI report generation after all execution layers are complete
 * @param analyticsContext - The analytics context containing report metadata
 * @param allResults - Results from all executed agents
 */
const runBiReport = async (analyticsContext: AnalyticsContext, allResults: any) => {
  try {
    // Sleep for 1 second before running the BI report
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Send message to iframe to trigger BI report generation
    // sendIframeMessage('RUN_BI_REPORT', {
    //   analyticsContext,
    //   executionResults: allResults,
    //   timestamp: new Date().toISOString()
    // });
    
  } catch (error) {
    console.error(' Error running BI report:', error);
  }
};

export const handleBiAgent = async (
  content: string,
  messages: (Message | ToolCall)[],
  analyticsContext: AnalyticsContext,
  onToolCallComplete: (toolCall: ToolCall) => void,
  dispatch: any
): Promise<{ message: Message; toolCalls: ToolCall[] }> => {
  const toolCalls: ToolCall[] = [];
  
  try {
    const previousConversation = JSON.stringify(messages);
    const agentsData = await getAgentsToBeUsed({
      prompt: content,
      type: analyticsContext.reportMetadata?.selectedReport,
      analyticsContext: analyticsContext,
      previous_conversation: previousConversation
    });

    if (agentsData && agentsData.length > 0) {
      const executionLayers = agentsData[0].executionLayers || [];
      let allResults: any = {};
      
      // Initialize execution layers in Redux
      const layers = executionLayers.map((layer: any, index: number) => ({
        layerIndex: index,
        agents: (layer.agents || layer).map((agent: any) => ({
          agent: agent.agent,
          status: 'pending' as const,
        })),
        status: 'pending' as const,
      }));
      
      dispatch(setExecutionLayers(layers));
      
      // Execute layers sequentially
      for (let i = 0; i < executionLayers.length; i++) {
        const layer = executionLayers[i];
        const layerResults = await executeLayer(
          layer,
          i,
          content,
          analyticsContext,
          previousConversation,
          allResults,
          dispatch,
          onToolCallComplete
        );
        

        allResults = { ...allResults, ...layerResults };
        
      }
      
      const successMessage = createAiMessage(
        `✅ BI Agent has successfully processed your request and executed ${Object.keys(allResults).length} tool(s) across ${executionLayers.length} layer(s). The results are now available in your analytics dashboard.`
      );
      
      await runBiReport(analyticsContext, allResults);
      
      return { message: successMessage, toolCalls };
    } else {
      const errorMessage = createAiMessage(
        '❌ BI Agent could not process the request. Please try rephrasing your query.'
      );
      return { message: errorMessage, toolCalls };
    }
  } catch (error) {
    
    const errorMessage = createAiMessage(
      '❌ BI Agent encountered an error while processing your request. Please try again.'
    );
    return { message: errorMessage, toolCalls };
  }
};

export const handleChatReset = () => {
  try {
    const message = {
      type: 'RESET_STATE',
      data: {
        timestamp: new Date().toISOString(),
      },
      source: 'AskAiDrawer'
    };
    sendMessage(IframeApps.GENERIC_ANALYTICS, message);
  } catch (error) {
    console.error('Failed to send chat reset message:', error);
  }
}; 